import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar } from 'lucide-react';
import api from '../lib/api';
import { messageHandler } from '../services/message-handler.service';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import BookingFilters from '../components/calendar/BookingFilters';
import BookingList from '../components/calendar/BookingList';
import BookingForm from '../components/calendar/BookingForm';
import BookingDetails from '../components/calendar/BookingDetails';
import ContactManager from '../components/contacts/ContactManager';
import { formatDate, formatDateTime } from '../lib/formatters';

// Types
interface CalendarBooking {
  id: number;
  client_name: string;
  service_type_id: number;
  status: 'booked' | 'confirmed' | 'canceled';
  contact_id?: number;
  contact_name?: string;
  contact_info?: string;
  service_name?: string;
  professional_name?: string;
  slots?: {
    date: string;
    start_time: string;
    end_time: string;
  }[];
  booked_at?: string;
}

interface BookingFilters {
  status?: string;
  date_start?: string;
  date_end?: string;
  professional_id?: number;
  service_type_id?: number;
  contact_id?: number;
  page?: number;
  limit?: number;
}

interface Contact {
  id: number;
  contact: string;
  name: string;
  channel: string;
}

type ModalType = 'create' | 'edit' | 'view' | 'contact' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 20;

function CalendarBookings() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingBooking, setEditingBooking] = useState<CalendarBooking | null>(null);
  const [filters, setFilters] = useState<BookingFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(new Date(), 'iso'));
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<number | null>(null);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<number | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [searchContact, setSearchContact] = useState<string>('');
  const [filteredServiceTypes, setFilteredServiceTypes] = useState<any[]>([]);
  const queryClient = useQueryClient();

  // Queries
  const { data: bookingsResponse, isLoading } = useQuery({
    queryKey: ['calendar-bookings', filters],
    queryFn: async () => {
      const response = await api.get('/calendar/bookings', { params: filters });
      return response.data;
    },
    keepPreviousData: true,
  });

  const { data: professionals } = useQuery({
    queryKey: ['calendar-professionals'],
    queryFn: async () => {
      const response = await api.get('/calendar/professionals');
      return response.data;
    },
  });

  const { data: serviceTypes } = useQuery({
    queryKey: ['calendar-service-types'],
    queryFn: async () => {
      const response = await api.get('/calendar/service-types');
      return response.data;
    },
  });

  const { data: availableSlots } = useQuery({
    queryKey: ['calendar-slots', selectedDate, selectedProfessionalId, selectedServiceTypeId],
    queryFn: async () => {
      if (!selectedDate || !selectedProfessionalId) return [];

      const params: any = {
        date_start: selectedDate,
        date_end: selectedDate,
        professional_id: selectedProfessionalId,
        available_only: true,
      };

      const response = await api.get('/calendar/slots', { params });
      return response.data;
    },
    enabled: !!selectedDate && !!selectedProfessionalId && modal.type === 'create',
  });

  const { data: contacts, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['contacts', searchContact],
    queryFn: async () => {
      const params = searchContact ? { search: searchContact } : {};
      const response = await api.get('/contacts', { params });
      return response.data.data;
    },
    enabled: modal.type === 'contact' || !!searchContact,
  });

  const { data: bookingDetails } = useQuery({
    queryKey: ['booking-details', editingBooking?.id],
    queryFn: async () => {
      if (!editingBooking?.id) return null;
      const response = await api.get(`/calendar/bookings/${editingBooking.id}`);
      return response.data;
    },
    enabled: !!editingBooking?.id && (modal.type === 'view' || modal.type === 'edit'),
  });

  // Filter service types based on selected professional
  useEffect(() => {
    if (selectedProfessionalId && serviceTypes) {
      // Get the professional's service types
      const professional = professionals?.find(p => p.id === selectedProfessionalId);
      if (professional && professional.service_types) {
        setFilteredServiceTypes(professional.service_types);
      } else {
        // Fallback to all service types if professional doesn't have specific ones
        setFilteredServiceTypes(serviceTypes);
      }
    } else {
      setFilteredServiceTypes([]);
    }
  }, [selectedProfessionalId, professionals, serviceTypes]);

  // Mutations
  const createBookingMutation = useMutation({
    mutationFn: async (data: Partial<CalendarBooking>) => {
      const response = await api.post('/calendar/bookings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-slots'] });
      messageHandler.success('Agendamento criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await api.put(`/calendar/bookings/${id}/status`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-details'] });
      messageHandler.success('Status do agendamento atualizado com sucesso!');
      if (modal.type === 'edit') {
        closeModal();
      }
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteBookingMutation = useMutation({
    mutationFn: async (id: number) => {
      // Use DELETE endpoint instead of updating status
      await api.delete(`/calendar/bookings/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
      messageHandler.success('Agendamento excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      client_name: formData.get('client_name') as string,
      service_type_id: Number(formData.get('service_type_id')),
      slot_ids: selectedSlots,
      status: formData.get('status') as 'booked' | 'confirmed',
      contact_id: selectedContactId || undefined,
    };

    if (editingBooking) {
      updateStatusMutation.mutate({
        id: editingBooking.id,
        status: data.status,
      });
    } else {
      createBookingMutation.mutate(data);
    }
  };

  const handleStatusChange = (booking: CalendarBooking, status: string) => {
    updateStatusMutation.mutate({
      id: booking.id,
      status: status as 'booked' | 'confirmed' | 'canceled',
    });
  };

  const handleDelete = (booking: CalendarBooking) => {
    messageHandler.confirm(
      'Excluir Agendamento',
      'Tem certeza que deseja excluir este agendamento? Esta ação não pode ser desfeita.',
      () => deleteBookingMutation.mutate(booking.id)
    );
  };

  const handleFilterChange = (newFilters: BookingFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSlotToggle = (slotId: number) => {
    if (selectedSlots.includes(slotId)) {
      setSelectedSlots(selectedSlots.filter(id => id !== slotId));
    } else {
      setSelectedSlots([...selectedSlots, slotId]);
    }
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContactId(contact.id);
    setModal({ type: 'create' });
  };

  const handleProfessionalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedProfessionalId(value ? Number(value) : null);
    setSelectedServiceTypeId(null); // Reset service type when professional changes
    setSelectedSlots([]);
  };

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedServiceTypeId(value ? Number(value) : null);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setSelectedSlots([]);
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingBooking(null);
    setSelectedSlots([]);
    setSelectedProfessionalId(null);
    setSelectedServiceTypeId(null);
    setSelectedContactId(null);
    setSearchContact('');
  };

  const bookings = bookingsResponse?.data || [];
  const pagination = bookingsResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof BookingFilters]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Agendamentos"
          description="Gerencie os agendamentos de serviços."
          actions={
            <Button
              onClick={() => {
                setEditingBooking(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Agendamento
            </Button>
          }
        />

        <BookingFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          professionals={professionals || []}
          serviceTypes={serviceTypes || []}
        />

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
          </span>
        </div>

        <BookingList
          bookings={bookings}
          isLoading={isLoading}
          isFiltered={isFiltered}
          onView={booking => {
            setEditingBooking(booking);
            setModal({ type: 'view' });
          }}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onCreateNew={() => {
            setEditingBooking(null);
            setModal({ type: 'create' });
          }}
        />

        {pagination && pagination.last_page > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.last_page}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Create Booking Modal */}
        <Modal
          isOpen={modal.type === 'create'}
          onClose={closeModal}
          title="Novo Agendamento"
          size="xl"
        >
          <BookingForm
            onSubmit={handleSubmit}
            selectedProfessionalId={selectedProfessionalId}
            selectedServiceTypeId={selectedServiceTypeId}
            selectedDate={selectedDate}
            selectedSlots={selectedSlots}
            selectedContactId={selectedContactId}
            availableSlots={availableSlots || []}
            professionals={professionals || []}
            serviceTypes={serviceTypes || []}
            filteredServiceTypes={filteredServiceTypes}
            contacts={contacts || []}
            onProfessionalChange={handleProfessionalChange}
            onServiceTypeChange={handleServiceTypeChange}
            onDateChange={handleDateChange}
            onSlotToggle={handleSlotToggle}
            onSelectContact={() => setModal({ type: 'contact' })}
            isLoading={createBookingMutation.isPending}
          />
        </Modal>

        {/* View/Edit Booking Modal */}
        <Modal
          isOpen={modal.type === 'view' || modal.type === 'edit'}
          onClose={closeModal}
          title={`${modal.type === 'view' ? 'Detalhes do' : 'Editar'} Agendamento`}
          size="lg"
        >
          <BookingDetails
            booking={bookingDetails}
            isLoading={!bookingDetails}
            isEditing={modal.type === 'edit'}
            onSubmit={handleSubmit}
            onEdit={() => setModal({ type: 'edit' })}
            onClose={closeModal}
            isUpdating={updateStatusMutation.isPending}
          />
        </Modal>

        {/* Contact Selection Modal */}
        <Modal
          isOpen={modal.type === 'contact'}
          onClose={() => setModal({ type: 'create' })}
          title="Gerenciar Contatos"
          size="xl"
        >
          <ContactManager
            contacts={contacts || []}
            isLoading={isLoadingContacts}
            searchTerm={searchContact}
            onSearchChange={setSearchContact}
            onSelectContact={handleSelectContact}
            onClose={() => setModal({ type: 'create' })}
            showCreateButton={true}
          />
        </Modal>
      </div>
    </div>
  );
}

export default CalendarBookings;
